import React, { useEffect, useMemo, useState } from "react";
import { TL } from "../i18n";
import client from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, Input, Badge } from "../components/ui";
import { num, money } from "../lib/holdingsMath";
import { Link } from "react-router-dom";
import { Lock, Users } from "lucide-react";

const tokens = (s) => String(s || "").toLowerCase().split(",").map((x) => x.trim()).filter(Boolean);

function project(annual, years, escPct, discPct) {
  let nominal = 0, npv = 0;
  const esc = num(escPct) / 100, disc = num(discPct) / 100;
  for (let t = 1; t <= years; t++) {
    const c = annual * Math.pow(1 + esc, t - 1);
    nominal += c;
    npv += c / Math.pow(1 + disc, t);
  }
  return { nominal, npv };
}

const ageOf = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (365.25 * 86400000));
};

export default function Recipients() {
  const { isUnlocked } = useApp();
  const allowed = isUnlocked("family_tree") && isUnlocked("benefits");
  const [persons, setPersons] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [horizon, setHorizon] = useState(20);
  const [esc, setEsc] = useState(3);
  const [disc, setDisc] = useState(5);
  const [lifeExp, setLifeExp] = useState(85);

  useEffect(() => {
    if (!allowed) return;
    client.get("/persons").then((r) => setPersons(r.data.map((p) => ({ id: p.id, ...(p.data || p) })))).catch(() => {});
    client.get("/benefits").then((r) => setBenefits(r.data.map((b) => ({ id: b.id, ...(b.data || b) })))).catch(() => {});
  }, [allowed]);

  const rows = useMemo(() => persons.map((p) => {
    const pg = tokens(p.groups);
    const matched = benefits.filter((b) => {
      const bg = tokens(b.groups);
      const byType = b.type && pg.includes(String(b.type).toLowerCase());
      return bg.some((g) => pg.includes(g)) || byType;
    });
    const monthly = matched.reduce((a, b) => a + num(b.amount), 0);
    const annual = monthly * 12;
    const age = ageOf(p.dob);
    let years = num(horizon);
    if (age != null && num(lifeExp) > 0) years = Math.max(0, Math.min(years, num(lifeExp) - age));
    const { nominal, npv } = project(annual, years, esc, disc);
    return { p, matched, monthly, annual, years, nominal, npv, age };
  }), [persons, benefits, horizon, esc, disc, lifeExp]);

  const totals = rows.reduce((a, r) => ({ annual: a.annual + r.annual, nominal: a.nominal + r.nominal, npv: a.npv + r.npv }), { annual: 0, nominal: 0, npv: 0 });

  if (!allowed) {
    return (
      <div className="space-y-4 fadeup" data-testid="recipients-locked">
        <h1 className="font-head text-2xl sm:text-3xl">{TL("Benefit Recipients & Projections")}</h1>
        <Card className="flex items-center gap-3 text-sm">
          <Lock size={18} className="text-[var(--primary)]" />
          <span>{TL("Unlock both the")} <b>{TL("Family Tree")}</b> {TL("and")} <b>{TL("Benefits")}</b> applications in the{" "}
            <Link to="/app/enabler" className="text-[var(--primary)] underline">{TL("Master Enabler")}</Link> {TL("to assign recipients and run actuarial projections.")}</span>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 fadeup">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-head text-2xl sm:text-3xl flex items-center gap-2"><Users size={24} /> {TL("Benefit Recipients & Projections")}</h1>
      </div>
      <p className="text-sm opacity-70 max-w-3xl">{TL("Individuals inherit every benefit whose group/category matches one of the groups assigned to them on the Family Tree — so a person placed in several groups receives several benefits. Costs below are escalated and discounted to present value (NPV).")}</p>

      <Card className="grid gap-4 sm:grid-cols-4">
        <Input label={TL("Projection horizon (years)")} type="number" value={horizon} onChange={(e) => setHorizon(e.target.value)} data-testid="proj-horizon" />
        <Input label={TL("Annual escalation %")} type="number" value={esc} onChange={(e) => setEsc(e.target.value)} data-testid="proj-esc" />
        <Input label={TL("Discount rate % (NPV)")} type="number" value={disc} onChange={(e) => setDisc(e.target.value)} data-testid="proj-disc" />
        <Input label={TL("Life expectancy age (cap, 0 = off)")} type="number" value={lifeExp} onChange={(e) => setLifeExp(e.target.value)} data-testid="proj-life" />
      </Card>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="space-y-1"><div className="text-xs opacity-60">{TL("Total annual benefit cost")}</div><div className="font-head text-2xl">{money(totals.annual)}</div></Card>
        <Card className="space-y-1"><div className="text-xs opacity-60">{TL("Projected nominal (horizon)")}</div><div className="font-head text-2xl">{money(totals.nominal)}</div></Card>
        <Card className="space-y-1"><div className="text-xs opacity-60">{TL("Present value (NPV)")}</div><div className="font-head text-2xl">{money(totals.npv)}</div></Card>
      </div>

      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm" data-testid="recipients-table">
          <thead><tr className="border-b border-black/10 dark:border-white/10">
            <th className="p-3 text-start font-medium opacity-70">{TL("Individual")}</th>
            <th className="p-3 text-start font-medium opacity-70">{TL("Groups")}</th>
            <th className="p-3 text-start font-medium opacity-70">{TL("Benefits")}</th>
            <th className="p-3 text-end font-medium opacity-70">{TL("Annual")}</th>
            <th className="p-3 text-end font-medium opacity-70">{TL("Yrs")}</th>
            <th className="p-3 text-end font-medium opacity-70">{TL("Nominal")}</th>
            <th className="p-3 text-end font-medium opacity-70">{TL("NPV")}</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="p-4 text-center opacity-50">{TL("No individuals yet. Add people with groups on the Family Tree.")}</td></tr>}
            {rows.map((r) => (
              <tr key={r.p.id} className="border-b border-black/5 dark:border-white/5" data-testid={`recipient-${r.p.id}`}>
                <td className="p-3">{[r.p.first, r.p.last].filter(Boolean).join(" ") || "Unnamed"}{r.age != null ? <span className="opacity-50"> · {r.age}y</span> : ""}</td>
                <td className="p-3"><div className="flex flex-wrap gap-1">{tokens(r.p.groups).map((g) => <Badge key={g} color="info">{g}</Badge>)}</div></td>
                <td className="p-3"><div className="flex flex-wrap gap-1">{r.matched.length === 0 ? <span className="opacity-40">{TL("none")}</span> : r.matched.map((b) => <Badge key={b.id}>{b.name}</Badge>)}</div></td>
                <td className="p-3 text-end">{money(r.annual)}</td>
                <td className="p-3 text-end">{r.years}</td>
                <td className="p-3 text-end">{money(r.nominal)}</td>
                <td className="p-3 text-end font-medium">{money(r.npv)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
